/*
  ATD - Ubicaciones · galería con lightbox (atd-locations1.js)

  Web component <atd-lightbox> GENÉRICO y reutilizable:
   - Cuadrícula de thumbnails que abren un <dialog> nativo (focus-trap + Esc del
     browser gratis) con la foto clicada.
   - Galería deslizable dentro del lightbox: track con scroll-snap nativo (swipe
     en móvil) + flechas prev/next (desktop) + flechas de teclado. El índice se
     deriva del scroll real (IntersectionObserver) para que el contador siga al
     dedo. Mismo patrón que el slider de atd-complementary1.
   - Cierra con: botón X, clic en el backdrop (fuera del panel) y Esc.
   - Scroll-lock del fondo con restauración (igual que atd-sizeguide1).
   - prefers-reduced-motion: sin scroll suave ni animación de cierre.

  Cada instancia es independiente: varias tiendas en la página = varios
  <atd-lightbox> que no se cruzan. No depende del bloque de Ubicaciones — se
  puede reusar tal cual en otros bloques/clientes (solo necesita el markup con
  los mismos data-attributes).
*/
(() => {
  if (customElements.get('atd-lightbox')) return;

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class AtdLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-atd-lb-dialog]');
      this.panel = this.querySelector('[data-atd-lb-panel]');
      this.track = this.querySelector('[data-atd-lb-track]');
      this.slides = Array.from(this.querySelectorAll('[data-atd-lb-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-atd-lb-open]'));
      this.closeBtn = this.querySelector('[data-atd-lb-close]');
      this.prevBtn = this.querySelector('[data-atd-lb-prev]');
      this.nextBtn = this.querySelector('[data-atd-lb-next]');
      this.currentEl = this.querySelector('[data-atd-lb-current]');

      if (!this.dialog || !this.track || this.slides.length === 0) return;

      this.index = 0;
      this._scrollY = 0;
      this._lastTrigger = null;

      this.#wireThumbs();
      this.#wireControls();
      this.#wireScrollSync();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }

    /* ---------------- Thumbnails abren en su índice ---------------- */
    #wireThumbs() {
      this.thumbs.forEach((btn) => {
        btn.addEventListener('click', () => {
          const i = parseInt(btn.dataset.atdLbOpen, 10) || 0;
          this._lastTrigger = btn;
          this.#open(i);
        });
      });
    }

    /* ---------------- Controles del lightbox ---------------- */
    #wireControls() {
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.#close());
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.#goTo(this.index - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.#goTo(this.index + 1));

      // Clic en el backdrop (fuera del panel) cierra.
      this.dialog.addEventListener('click', (e) => {
        if (!this.panel.contains(e.target)) this.#close();
      });

      // Esc nativo del <dialog> dispara "cancel": lo interceptamos para correr
      // nuestro cierre (restaura scroll) en vez del close() crudo del browser.
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.#close();
      });

      // Flechas del teclado mientras el lightbox está abierto.
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.#goTo(this.index - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.#goTo(this.index + 1);
        }
      });
    }

    /* ---------------- Sincroniza índice con el scroll real ---------------- */
    #wireScrollSync() {
      if (this.slides.length < 2) return;

      this._io = new IntersectionObserver(
        (entries) => {
          // La slide más visible manda. Solo actualizamos contador/flechas; NO
          // re-scrolleamos (eso pelearía con el dedo del usuario).
          let best = null;
          let bestRatio = 0;
          entries.forEach((en) => {
            if (en.intersectionRatio > bestRatio) {
              bestRatio = en.intersectionRatio;
              best = en.target;
            }
          });
          if (best) {
            const i = this.slides.indexOf(best);
            if (i >= 0 && i !== this.index) {
              this.index = i;
              this.#syncUi();
            }
          }
        },
        { root: this.track, threshold: [0.5, 0.75, 1] }
      );
      this.slides.forEach((s) => this._io.observe(s));
    }

    /* ---------------- Abrir / cerrar ---------------- */
    #open(i) {
      if (this.dialog.open) return;
      this.index = Math.max(0, Math.min(i, this.slides.length - 1));
      this._scrollY = window.scrollY;

      this.dialog.showModal(); // focus-trap nativo

      // Scroll-lock del fondo sin saltos de layout.
      document.body.style.top = `-${this._scrollY}px`;
      document.body.setAttribute('data-atd-lb-lock', '');

      // El track ya es medible (el dialog dejó de estar display:none): saltar a la
      // slide pedida sin animación y refrescar UI. rAF para que el layout asiente.
      requestAnimationFrame(() => {
        this.#scrollToIndex(this.index, 'auto');
        this.#syncUi();
      });
    }

    #close() {
      if (!this.dialog.open) return;
      this.dialog.classList.add('is-closing');

      const finish = () => {
        this.dialog.classList.remove('is-closing');
        this.dialog.close();
        document.body.removeAttribute('data-atd-lb-lock');
        document.body.style.top = '';
        window.scrollTo({ top: this._scrollY, behavior: 'instant' });
        if (this._lastTrigger) this._lastTrigger.focus({ preventScroll: true });
      };

      if (REDUCE) {
        finish();
        return;
      }

      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        this.panel.removeEventListener('animationend', onEnd);
        finish();
      };
      this.panel.addEventListener('animationend', onEnd);
      setTimeout(onEnd, 320); // fallback por si no dispara animationend
    }

    /* ---------------- Navegación entre slides ---------------- */
    #goTo(i) {
      // Loop: -1 → última, length → primera.
      const n = this.slides.length;
      const target = ((i % n) + n) % n;
      this.index = target;
      this.#scrollToIndex(target, REDUCE ? 'auto' : 'smooth');
      this.#syncUi();
    }

    #scrollToIndex(i, behavior) {
      const slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior });
    }

    #syncUi() {
      if (this.currentEl) this.currentEl.textContent = String(this.index + 1);
    }
  }

  customElements.define('atd-lightbox', AtdLightbox);
})();
